import React, { useState } from "react";
import { Heart, Share2, Bookmark, Plus, Star, Flame, Timer, ChefHat, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { likeWorkout, likeRecipe } from "../lib/api";
import { toast } from "sonner";

const FeedCard = ({ item }) => {
    const isWorkout = item._kind === "workout";
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [liked, setLiked] = useState(false);
    const [saved, setSaved] = useState(false);
    const [likes, setLikes] = useState(item.likes || 0);
    const [pop, setPop] = useState(false);

    const handleLike = async () => {
        if (liked) return;
        setLiked(true);
        setPop(true);
        setLikes((l) => l + 1);
        setTimeout(() => setPop(false), 400);
        try {
            if (isWorkout) await likeWorkout(item.id);
            else await likeRecipe(item.id);
        } catch {}
    };

    const handleShare = () => {
        const url = `${window.location.origin}/${isWorkout ? "workout" : "recipe"}/${item.id}`;
        if (navigator.share) {
            navigator.share({ title: item.title, url }).catch(() => {});
        } else {
            navigator.clipboard?.writeText(url);
            toast.success("Link copied to clipboard");
        }
    };

    const handlePrimary = () => {
        if (isWorkout) {
            // Quick-subscribe to monthly tier as default; user can pick others on detail page
            const monthly = item.tiers.find((t) => t.id === "monthly") || item.tiers[0];
            addItem(item, monthly);
            toast.success(`${item.title} — ${monthly.name} added`);
        } else {
            navigate(`/recipe/${item.id}`);
        }
    };

    const author = isWorkout ? item.creator : item.chef;
    const avatar = isWorkout ? item.creator_avatar : item.chef_avatar;
    const monthly = isWorkout ? item.tiers.find((t) => t.id === "monthly") || item.tiers[0] : null;

    return (
        <section
            data-testid={`feed-card-${item.id}`}
            className="relative h-[100dvh] w-full snap-start overflow-hidden bg-black"
        >
            <img
                src={item.image_url}
                alt={item.title}
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
            />
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0A0A0A]/80 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-[72%] bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />

            {/* Top header */}
            <div className="relative z-10 flex items-center justify-between px-4 pt-5">
                <span className="font-display text-2xl tracking-wider">FLEXFEED</span>
                <span
                    className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                        isWorkout
                            ? "border-[#E1FF01] text-[#E1FF01]"
                            : "border-white/40 text-white"
                    }`}
                >
                    {isWorkout ? "Subscription" : "Recipe"}
                </span>
            </div>

            {/* Right action rail */}
            <div className="absolute right-3 bottom-44 z-20 flex flex-col items-center gap-5">
                <button
                    data-testid={`like-btn-${item.id}`}
                    onClick={handleLike}
                    className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                >
                    <span
                        className={`w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/15 flex items-center justify-center ${
                            pop ? "animate-heart-pop" : ""
                        }`}
                    >
                        <Heart
                            size={22}
                            strokeWidth={2.2}
                            className={liked ? "fill-[#FF3B30] text-[#FF3B30]" : "text-white"}
                        />
                    </span>
                    <span className="text-[11px] font-semibold">
                        {likes >= 1000 ? `${(likes / 1000).toFixed(1)}K` : likes}
                    </span>
                </button>
                <button
                    data-testid={`save-btn-${item.id}`}
                    onClick={() => setSaved((s) => !s)}
                    className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                >
                    <span className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/15 flex items-center justify-center">
                        <Bookmark
                            size={22}
                            strokeWidth={2.2}
                            className={saved ? "fill-[#E1FF01] text-[#E1FF01]" : "text-white"}
                        />
                    </span>
                    <span className="text-[11px] font-semibold">Save</span>
                </button>
                <button
                    data-testid={`share-btn-${item.id}`}
                    onClick={handleShare}
                    className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                >
                    <span className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/15 flex items-center justify-center">
                        <Share2 size={22} strokeWidth={2.2} className="text-white" />
                    </span>
                    <span className="text-[11px] font-semibold">Share</span>
                </button>
            </div>

            {/* Bottom info */}
            <div className="absolute inset-x-0 bottom-28 z-10 px-5 pb-4">
                <div className="flex items-center gap-2 mb-3">
                    <img
                        src={avatar}
                        alt={author}
                        className="w-9 h-9 rounded-full object-cover border border-white/30"
                    />
                    <div className="flex flex-col leading-tight">
                        <span className="text-sm font-semibold flex items-center gap-1">
                            {!isWorkout && <ChefHat size={12} className="text-[#E1FF01]" />}
                            @{author?.toLowerCase().replace(/\s+/g, "")}
                        </span>
                        {isWorkout ? (
                            <span className="text-xs text-white/60 flex items-center gap-1">
                                <Star size={11} className="fill-[#E1FF01] text-[#E1FF01]" />
                                {item.rating} · {item.reviews_count} reviews
                            </span>
                        ) : (
                            <span className="text-xs text-white/60">
                                Pairs with workout subscription
                            </span>
                        )}
                    </div>
                </div>

                <h2 className="font-display text-4xl uppercase leading-[0.95] mb-2">
                    {item.title}
                </h2>
                <p className="text-sm text-white/80 line-clamp-2 max-w-[78%] mb-3">
                    {isWorkout ? item.description : item.summary}
                </p>

                <div className="flex flex-wrap items-center gap-2 mb-1">
                    {isWorkout ? (
                        <>
                            {item.duration_min && (
                                <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 border border-white/10 flex items-center gap-1">
                                    <Timer size={12} /> {item.duration_min} min
                                </span>
                            )}
                            {item.intensity && (
                                <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 border border-white/10 flex items-center gap-1">
                                    <Flame size={12} className="text-[#E1FF01]" /> {item.intensity}
                                </span>
                            )}
                        </>
                    ) : (
                        <>
                            <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 border border-white/10 flex items-center gap-1">
                                <Flame size={12} className="text-[#E1FF01]" /> {item.calories} kcal
                            </span>
                            <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 border border-white/10 flex items-center gap-1">
                                <Timer size={12} /> {item.prep_min} min
                            </span>
                            <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 border border-white/10">
                                P {item.protein_g}g
                            </span>
                        </>
                    )}
                </div>
            </div>

            {/* Bottom CTA */}
            <div className="absolute inset-x-0 bottom-0 z-20 px-5 pb-24">
                {isWorkout ? (
                    <div className="space-y-2">
                        <button
                            data-testid={`add-to-cart-${item.id}`}
                            onClick={handlePrimary}
                            className="w-full bg-[#E1FF01] hover:bg-[#CDE800] text-[#0A0A0A] font-display text-2xl uppercase tracking-wider py-4 rounded-md flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                        >
                            <Plus size={22} strokeWidth={3} />
                            Subscribe ${monthly.price}/mo
                        </button>
                        <button
                            onClick={() => navigate(`/workout/${item.id}`)}
                            data-testid={`open-workout-${item.id}`}
                            className="w-full text-xs uppercase tracking-widest text-white/70 underline-offset-4 hover:underline py-1"
                        >
                            See all plans →
                        </button>
                    </div>
                ) : (
                    <button
                        data-testid={`open-recipe-${item.id}`}
                        onClick={handlePrimary}
                        className="w-full bg-white text-[#0A0A0A] font-display text-2xl uppercase tracking-wider py-4 rounded-md flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                    >
                        View Recipe <ChevronRight size={22} strokeWidth={3} />
                    </button>
                )}
            </div>
        </section>
    );
};

export default FeedCard;
